import { useEffect } from "react";
import { MapPin } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";

const upsertMeta = (kind: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`);
  const created = !el;
  const prev = el?.getAttribute("content") ?? "";
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute("content", prev);
  };
};

const ComingSoonLocation = ({ city, slug }: { city: string; slug: string }) => {
  useEffect(() => {
    const prevTitle = document.title;
    const title = `Rubbish Removal ${city} | Coming Soon | OZ WASTE CO.`;
    const description = `OZ WASTE CO. rubbish removal is coming soon to ${city}. In the meantime, get in touch with our Melbourne team.`;
    const url = `https://ozwasteco.com.au/${slug}`;
    document.title = title;
    const desc = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? "";
    desc?.setAttribute("content", description);
    const cleanups = [
      upsertMeta("property", "og:title", title),
      upsertMeta("property", "og:description", description),
      upsertMeta("property", "og:url", url),
      upsertMeta("name", "twitter:title", title),
      upsertMeta("name", "twitter:description", description),
    ];
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") ?? "";
    canonical?.setAttribute("href", url);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      desc?.setAttribute("content", prevDesc);
      cleanups.forEach((fn) => fn());
      if (canonical) canonical.setAttribute("href", prevCanonical);
    };
  }, [city, slug]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />
      <main className="flex-1">
        <section className="bg-[#FDC00E] relative overflow-hidden flex-1">
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.55),transparent_55%)]"
            aria-hidden="true"
          />
          <div className="container relative py-24 md:py-36">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 bg-orange text-white text-xs md:text-sm font-black tracking-widest uppercase px-4 py-2 rounded-md">
                <MapPin className="w-4 h-4" /> Launching Soon in {city}
              </span>
              <h1 className="mt-6 text-4xl md:text-5xl font-black text-[#1a1a1a] tracking-tight leading-tight">
                Coming Soon to Your Area
              </h1>
              <p className="mt-6 text-lg text-[#1a1a1a]/80 leading-relaxed">
                OZ WASTE CO. rubbish removal isn't available in {city} just yet, but we're on our
                way. In the meantime, get in touch with our Melbourne team and we'll let you know
                as soon as we launch near you.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/#quote"
                  className="bg-[#1a1a1a] text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors text-center"
                >
                  Get a Free Quote
                </a>
                <a
                  href="/"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange/10 hover:border-orange hover:text-orange-deep transition-colors"
                >
                  Back to Homepage
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default ComingSoonLocation;
